import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Save, Send, Globe, Server, Download, RefreshCw, ShieldCheck, Plus, X } from 'lucide-react';
import { getToken } from '../lib/api';
import { useToast } from '../components/Toast';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

export default function Settings() {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<any>({});
  const [newIp, setNewIp] = useState('');

  const settings = useQuery<any>({ queryKey: ['settings'], queryFn: () => fJson('/api/settings') });
  useEffect(() => { if (settings.data) setForm(settings.data); }, [settings.data]);

  const save = useMutation({
    mutationFn: () => fJson('/api/settings', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings'] }); toast('success', 'Settings saved'); },
    onError: (e: any) => toast('error', e.message),
  });

  const test = useMutation({
    mutationFn: async () => { await fJson('/api/settings', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) }); return fJson('/api/settings/test-notification', { method: 'POST' }); },
    onSuccess: (r: any) => toast(r.ok ? 'success' : 'error', r.ok ? ('Test pushed to ' + r.sent_to) : (r.error || 'Test failed')),
    onError: (e: any) => toast('error', e.message),
  });

  const pullNow = useMutation({
    mutationFn: async () => { await fJson('/api/settings', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) }); return fJson('/api/channel/pull', { method: 'POST' }); },
    onSuccess: () => toast('success', 'Checking the channel — newer installers pull in the background'),
    onError: (e: any) => toast('error', e.message),
  });

  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const wlList: string[] = (form.panel_ip_whitelist || '').split(',').map((s: string) => s.trim()).filter(Boolean);
  const setWl = (arr: string[]) => set('panel_ip_whitelist', arr.join(','));
  const addWl = () => { const v = newIp.trim(); if (v && !wlList.includes(v)) { setWl([...wlList, v]); setNewIp(''); } };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Settings</h1>
        <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-2"><Save size={14} /> Save</button>
      </div>
      <p className="text-sm text-textSecondary">Per-deployment configuration — this server's own credentials and destinations. A fresh install configures itself entirely from here.</p>

      <div className="card p-4 space-y-4">
        <div className="font-medium flex items-center gap-2"><ShieldCheck size={16} /> Panel Access — IP Allow-List</div>
        <p className="text-xs text-textSecondary">Restrict who can reach this web panel by IP / CIDR. Only listed addresses can load the panel or sign in over the internet. Your internal LAN access (<code>192.168.x</code>) is always allowed, and SBCs / phones are never affected.</p>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.panel_whitelist_enabled === 'true'} onChange={e => set('panel_whitelist_enabled', e.target.checked ? 'true' : 'false')} />
          Enforce the allow-list <span className="text-textSecondary">(off = panel open to any IP)</span>
        </label>
        <div className="space-y-2">
          {wlList.length === 0 && <div className="text-xs text-warning">No IPs listed — with enforcement on, all remote access is blocked (LAN still works).</div>}
          {wlList.map((ip: string) => (
            <div key={ip} className="flex items-center gap-2">
              <code className="flex-1 bg-elevated rounded px-2 py-1 text-xs font-mono">{ip}</code>
              <button onClick={() => setWl(wlList.filter((x: string) => x !== ip))} className="btn-ghost p-1" title="Remove"><X size={14} /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input className="input flex-1 font-mono text-xs" value={newIp} onChange={e => setNewIp(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addWl(); } }} placeholder="e.g. 203.0.113.7  or  203.0.113.0/24" />
          <button onClick={addWl} className="btn-ghost inline-flex items-center gap-1"><Plus size={14} /> Add</button>
        </div>
        <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-2"><Save size={14} /> Save allow-list</button>
      </div>

      <div className="card p-4 space-y-4">
        <div className="font-medium flex items-center gap-2"><Bell size={16} /> Notifications</div>
        <p className="text-xs text-textSecondary">Where system alerts (SBC offline/online, etc.) are pushed. Uses <b>ntfy</b> — install the free ntfy app on your phone and subscribe to your topic to receive them.</p>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.notify_enabled !== 'false'} onChange={e => set('notify_enabled', e.target.checked ? 'true' : 'false')} />
          Enable push notifications
        </label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-textSecondary mb-1">ntfy server</label>
            <input className="input w-full" value={form.notify_ntfy_url || ''} onChange={e => set('notify_ntfy_url', e.target.value)} placeholder="https://ntfy.sh" />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">ntfy topic (your own, unique)</label>
            <input className="input w-full font-mono text-xs" value={form.notify_ntfy_topic || ''} onChange={e => set('notify_ntfy_topic', e.target.value)} placeholder="stratus-alerts-acme-7x9k2" />
          </div>
        </div>
        <div>
          <label className="block text-sm text-textSecondary mb-1">Alert email (optional)</label>
          <input className="input w-full" value={form.notify_email_to || ''} onChange={e => set('notify_email_to', e.target.value)} placeholder="you@example.com" />
          <p className="text-xs text-textSecondary mt-1">The mail server (SMTP) is configured under <a href="/system/email" className="text-primary hover:underline">System → Email</a>.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-2"><Save size={14} /> Save</button>
          <button onClick={() => test.mutate()} disabled={test.isPending} className="btn-ghost inline-flex items-center gap-2"><Send size={14} /> {test.isPending ? 'Sending…' : 'Save + send test'}</button>
        </div>
      </div>

      <div className="card p-4 space-y-4">
        <div className="font-medium flex items-center gap-2"><Globe size={16} /> Network &amp; Tunnel</div>
        <p className="text-xs text-textSecondary">How branch SBCs reach this server. Use a <b>DNS name</b> for the tunnel endpoint so it survives a server move / IP change — then a restored backup on a new box reconnects every SBC without re-issuing installers.</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-textSecondary mb-1">WireGuard endpoint (host:port SBCs dial out to)</label>
            <input className="input w-full font-mono text-xs" value={form.wg_endpoint || ''} onChange={e => set('wg_endpoint', e.target.value)} placeholder="sbc.yourdomain.com:51821" />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Public hostname / IP</label>
            <input className="input w-full font-mono text-xs" value={form.public_hostname || ''} onChange={e => set('public_hostname', e.target.value)} placeholder="yourdomain.com" />
          </div>
        </div>
        <div>
          <label className="block text-sm text-textSecondary mb-1">PBX URL (baked into SBC configs; reached over the tunnel)</label>
          <input className="input w-full font-mono text-xs" value={form.pbx_url || ''} onChange={e => set('pbx_url', e.target.value)} placeholder="http://192.168.2.66:3500" />
        </div>
        <div className="text-xs text-textSecondary">New baked installers use these immediately. Existing SBCs keep their endpoint until re-issued — which is why a DNS name is the move.</div>
        <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-2"><Save size={14} /> Save</button>
      </div>

      <div className="card p-4 space-y-3">
        <div className="font-medium flex items-center gap-2"><Server size={16} /> Server Installer</div>
        <p className="text-xs text-textSecondary">Stand up a brand-new Stratus server on a fresh Debian 12 box. It installs the full stack (FusionPBX/FreeSWITCH/Kamailio/Postgres/Node/nginx/WireGuard) and restores a backup. The download is always the latest version — revising it updates what everyone pulls.</p>
        <div className="text-xs text-warning">v1 — validate on a throwaway box before production use.</div>
        <div className="bg-elevated rounded p-2 font-mono text-xs overflow-x-auto">curl -fsSL {window.location.origin}/api/server/install.sh | sudo bash -s -- &lt;backup-url&gt;</div>
        <a href={`${window.location.origin}/api/server/install.sh`} download className="btn-ghost inline-flex items-center gap-2 w-fit"><Download size={14} /> Download install script</a>
      </div>

      <div className="card p-4 space-y-4">
        <div className="font-medium flex items-center gap-2"><RefreshCw size={16} /> Update Channel</div>
        <p className="text-xs text-textSecondary">Keeps installers current automatically. Point a deployment at the central master's URL and it pulls the latest <b>SBC installer, SBC agent, and server installer</b> whenever you revise them (checked every 30 min + on demand). The master itself leaves this off — it <b>is</b> the source. SBC agents already auto-update from this server; this keeps the downloadable installers fresh too.</p>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.update_channel_enabled === 'true'} onChange={e => set('update_channel_enabled', e.target.checked ? 'true' : 'false')} />
          Pull installers from a central channel
        </label>
        <div>
          <label className="block text-sm text-textSecondary mb-1">Central channel URL (the master server)</label>
          <input className="input w-full font-mono text-xs" value={form.update_channel_url || ''} onChange={e => set('update_channel_url', e.target.value)} placeholder="https://central.yourdomain.com" />
        </div>
        <div className="flex gap-2">
          <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-2"><Save size={14} /> Save</button>
          <button onClick={() => pullNow.mutate()} className="btn-ghost inline-flex items-center gap-2"><RefreshCw size={14} /> Check for updates now</button>
        </div>
      </div>
    </div>
  );
}
